
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import OfflineLogger from '@/utils/offlineLogger';

export function useSafeNavigation() {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const safeNavigate = async (
    path: string, 
    options?: { 
      replace?: boolean; 
      validateBeforeNav?: () => Promise<boolean> | boolean;
      timeout?: number;
    }
  ) => {
    if (isNavigating) {
      OfflineLogger.warn('useSafeNavigation', 'Navigation already in progress, ignoring');
      return false;
    }

    try {
      setIsNavigating(true);
      
      // Optional validation before navigation
      if (options?.validateBeforeNav) {
        const isValid = await Promise.resolve(options.validateBeforeNav());
        if (!isValid) {
          OfflineLogger.warn('useSafeNavigation', 'Navigation validation failed');
          return false;
        }
      }

      // Navigation with timeout
      const timeout = options?.timeout || 5000;
      const navigationPromise = new Promise<void>((resolve) => {
        navigate(path, { replace: options?.replace });
        // Give time for the navigation to process
        setTimeout(resolve, 100);
      });

      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => reject(new Error('Navigation timeout')), timeout);
      });

      await Promise.race([navigationPromise, timeoutPromise]);
      
      OfflineLogger.info('useSafeNavigation', `Successfully navigated to ${path}`);
      return true;
    } catch (error) {
      OfflineLogger.error('useSafeNavigation', `Navigation failed: ${error}`);
      return false;
    } finally {
      // Reset navigation state after a delay
      setTimeout(() => setIsNavigating(false), 1000);
    }
  };

  return {
    safeNavigate,
    isNavigating
  };
}
